import React from 'react';
import { cn } from '@/lib/utils';

interface LoadingSpinnerProps {
  size?: 'sm' | 'md' | 'lg' | 'xl';
  variant?: 'primary' | 'light' | 'subtle';
  className?: string;
  text?: string;
  showText?: boolean;
}

const sizeClasses = {
  sm: 'h-4 w-4',
  md: 'h-6 w-6', 
  lg: 'h-8 w-8',
  xl: 'h-12 w-12'
};

const variantClasses = {
  primary: 'border-primary border-r-transparent',
  light: 'border-white border-r-transparent',
  subtle: 'border-gray-300 border-r-transparent'
};

const textSizeClasses = {
  sm: 'text-xs',
  md: 'text-sm',
  lg: 'text-base',
  xl: 'text-lg'
};

export function LoadingSpinner({ 
  size = 'md', 
  variant = 'primary', 
  className,
  text,
  showText = true 
}: LoadingSpinnerProps) {
  return (
    <div className={cn("flex flex-col items-center justify-center gap-2", className)}>
      <div 
        className={cn(
          "animate-spin rounded-full border-2",
          sizeClasses[size],
          variantClasses[variant]
        )}
      />
      {showText && text && (
        <p className={cn(
          "text-gray-600 font-medium",
          textSizeClasses[size]
        )}>
          {text}
        </p>
      )}
    </div>
  );
}

// Specialized loading components for common use cases
export function MapLoadingSpinner({ className }: { className?: string }) {
  return (
    <div
      className={cn(
        "absolute inset-0 bg-white/80 backdrop-blur-sm flex items-center justify-center z-10",
        className
      )}
      role="status"
      aria-live="polite"
      aria-label="Loading map data"
    >
      <LoadingSpinner 
        size="lg" 
        variant="primary" 
        text="Loading map data..."
        className="bg-white/90 rounded-xl p-6 shadow-lg min-w-[180px]"
      />
    </div>
  );
}

export function BusinessCardsLoadingSpinner({ className }: { className?: string }) {
  return (
    <div className={cn(
      "flex items-center justify-center py-12",
      className
    )}>
      <LoadingSpinner 
        size="lg" 
        variant="primary" 
        text="Loading wildlife experiences..."
        className="bg-gray-50/80 rounded-lg p-8 border border-gray-200"
      />
    </div>
  );
}

// Skeleton loading for business cards
export function BusinessCardSkeleton() {
  return (
    <div className="bg-white rounded-xl border border-gray-200 shadow-sm p-4 animate-pulse">
      <div className="aspect-[16/9] bg-gray-200 rounded-lg mb-4"></div>
      <div className="space-y-3">
        <div className="h-4 bg-gray-200 rounded w-3/4"></div>
        <div className="h-3 bg-gray-200 rounded w-1/2"></div>
        <div className="flex gap-2">
          <div className="h-6 bg-gray-200 rounded-full w-16"></div>
          <div className="h-6 bg-gray-200 rounded-full w-20"></div>
        </div>
      </div>
    </div>
  );
}

export function BusinessCardsSkeletonGrid({ count = 3 }: { count?: number }) {
  return (
    <div className="space-y-4 p-4">
      {Array.from({ length: count }, (_, i) => (
        <BusinessCardSkeleton key={i} />
      ))}
    </div>
  );
}
